import { createContext, type ReactNode, useCallback, useContext, useEffect, useMemo, useState } from "react";
import {
  applyTheme,
  type ResolvedTheme,
  readStoredPreference,
  resolveTheme,
  storePreference,
  systemPrefersDark,
  type ThemePreference,
} from "./theme";

interface ThemeContextValue {
  preference: ThemePreference;
  /** What's actually showing (system preference resolved). */
  resolved: ResolvedTheme;
  setPreference: (preference: ThemePreference) => void;
}

const ThemeContext = createContext<ThemeContextValue | null>(null);

export function useTheme(): ThemeContextValue {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error("useTheme must be used within <ThemeProvider>");
  return ctx;
}

/**
 * Owns the theme for the whole app, signed in or not. Local-first: the
 * stored preference applies instantly (index.html already applied it before
 * React loaded); <ThemeSync> adopts the user's saved backend preference.
 */
export function ThemeProvider({ children }: Readonly<{ children: ReactNode }>) {
  const [preference, setPreference] = useState<ThemePreference>(readStoredPreference);
  const [prefersDark, setPrefersDark] = useState(systemPrefersDark);
  const resolved = resolveTheme(preference, prefersDark);

  // Follow OS changes live while on "system".
  useEffect(() => {
    const query = globalThis.matchMedia?.("(prefers-color-scheme: dark)");
    if (!query) return;
    const onChange = (e: MediaQueryListEvent) => setPrefersDark(e.matches);
    query.addEventListener("change", onChange);
    return () => query.removeEventListener("change", onChange);
  }, []);

  // Sync the DOM (an external system) with the resolved theme.
  useEffect(() => applyTheme(resolved), [resolved]);

  const choosePreference = useCallback((next: ThemePreference) => {
    storePreference(next);
    setPreference(next);
  }, []);

  const value = useMemo(
    () => ({ preference, resolved, setPreference: choosePreference }),
    [preference, resolved, choosePreference],
  );
  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}
